import { Fragment, useState } from 'react';
import type { ProvisioningPreview } from '@arcnet/contracts';
import { useDevices, useOpenProvisioning, useDeleteDevice } from '../../lib/api/hooks-extension.js';
import { useProvisioningVendors, usePreviewConfig } from '../../lib/api/hooks-provisioning.js';
import {
  TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge, SectionHead,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { relativeTime } from '../../lib/format.js';
import { VENDOR_LABEL, windowOpen, Window, Preview } from './ProvisioningBits.js';

/**
 * Provisioning: how handsets get their configuration.
 *
 * The portal half of the provisioning module (API: modules/provisioning). It
 * answers what the Handsets list cannot: has this phone ever fetched a
 * configuration, may it fetch one now, and what exactly will it be given.
 *
 * Handsets themselves -- MAC, model, which extensions sit on which line key --
 * are set up on each extension, under Phone setup. That is the phone's identity;
 * this is its supply of configuration, across the account. A handset left on no
 * extension has nowhere else to be seen, so it can be removed here.
 */
export function ProvisioningTab({ tenantId, onNavigate }: {
  tenantId: string;
  onNavigate?: (section: string) => void;
}) {
  const can = useSession((s) => s.can);
  const devices = useDevices(tenantId);
  const vendors = useProvisioningVendors();
  const open = useOpenProvisioning(tenantId);
  const del = useDeleteDevice(tenantId);
  const preview = usePreviewConfig(tenantId);
  const step = useStepUp();
  const [shown, setShown] = useState<{ deviceId: string; data: ProvisioningPreview } | null>(null);

  const rows = devices.data?.data ?? [];
  const mayOpen = can('device:provision');
  const mayWrite = can('device:write');
  const mayReveal = can('extension:credentials:reveal');
  const generated = (vendors.data?.data ?? []).filter((v) => v.generates_config).map((v) => v.label);

  // Never fetched, and cannot: somebody has to open a window while standing at
  // the phone. Said once at the top, because the handset itself shows nothing
  // but a failure to register.
  const stuck = rows.filter((d) => d.status === 'unprovisioned' && !windowOpen(d) && !d.address_pinned);

  const load = async (deviceId: string, reveal: boolean) => {
    await step.run(async () => {
      const data = await preview.mutateAsync({ deviceId, reveal });
      setShown({ deviceId, data });
      return data;
    });
  };

  return (
    <>
      <SectionHead title="Provisioning"
        sub={generated.length
          ? `How handsets fetch their configuration. ${generated.join(' and ')} receive a generated file; other makes get the settings listed for entering by hand.`
          : 'How handsets fetch their configuration, and whether they can right now.'} />

      {devices.error && <ErrorBanner error={devices.error} />}
      <StepUpPrompt step={step} what="Showing a configuration with its SIP password" />

      {stuck.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {stuck.length} handset{stuck.length === 1 ? ' has' : 's have'} never fetched a
            configuration. Allow provisioning while the phone is on the network.
          </span>
        </div>
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Handset</th><th>Phone</th><th>Configuration</th>
                <th>Last fetched</th><th>Window</th><th />
              </tr>
            </thead>
            {devices.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rows.map((d) => (
                  <Fragment key={d.id}>
                    <tr>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{d.label ?? d.mac_address ?? 'Handset'}</span>
                          {d.label && d.mac_address && (
                            <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{d.mac_address}</span>
                          )}
                          {d.lines.length === 0 && (
                            <span className="arc-subtle" style={{ fontSize: 11.5 }}>on no extension</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="arc-stack">
                          <span>{VENDOR_LABEL[d.vendor] ?? d.vendor}</span>
                          {d.model && <span className="arc-subtle" style={{ fontSize: 11.5 }}>{d.model}</span>}
                        </div>
                      </td>
                      <td><StatusBadge status={d.status} /></td>
                      <td className="arc-muted">
                        {d.provisioned_at ? relativeTime(d.provisioned_at) : <span className="arc-subtle">never</span>}
                      </td>
                      <td><Window device={d} /></td>
                      <td className="arc-td-right">
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            disabled={preview.isPending}
                            onClick={() => {
                              if (shown?.deviceId === d.id) setShown(null);
                              else void load(d.id, false);
                            }}>
                            {shown?.deviceId === d.id ? 'Close' : 'Preview'}
                          </button>
                          {mayOpen && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              disabled={open.isPending}
                              title="Let this handset fetch its configuration again, for 30 minutes"
                              onClick={() => {
                                if (confirm(`Allow ${d.label ?? d.mac_address ?? 'this handset'} to provision for the next 30 minutes? The URL does not change.`)) {
                                  open.mutate(d.id);
                                }
                              }}>
                              Allow provisioning
                            </button>
                          )}
                          {mayWrite && d.lines.length === 0 && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              disabled={del.isPending}
                              onClick={() => {
                                if (confirm(`Remove ${d.label ?? d.mac_address ?? 'this handset'}? It is on no extension.`)) {
                                  del.mutate(d.id);
                                }
                              }}>
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {shown?.deviceId === d.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: 12, background: 'var(--arc-surface-2)' }}>
                          <Preview data={shown.data} canReveal={mayReveal}
                            onReveal={() => void load(d.id, true)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!devices.isLoading && rows.length === 0 && (
          <Empty icon="phone" title="No handsets"
            hint="Add a phone by its MAC address from an extension's Phone setup."
            {...(onNavigate ? {
              action: (
                <button type="button" className="arc-btn arc-btn-sm" onClick={() => onNavigate('extensions')}>
                  Go to Extensions
                </button>
              ),
            } : {})} />
        )}
      </div>
    </>
  );
}
